import { PASSWORD_MIN_LENGTH, changePasswordSchema, type ChangePasswordRequest, type MeResponse } from '@kominiarz/shared';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, LogOut } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { setCurrentUser, useCurrentUser, useLogout } from '../auth/useAuth';
import { Alert } from '../components/Alert';
import { BrandMark } from '../components/BrandMark';
import { Button } from '../components/Button';
import { PasswordInput } from '../components/form/PasswordInput';
import { PageBody } from '../components/PageBody';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { api, ApiError, errorMessage } from '../lib/api';
import loginStyles from './LoginPage.module.css';
import styles from './ChangePasswordPage.module.css';

const NEW_PASSWORD_HINT = `Co najmniej ${PASSWORD_MIN_LENGTH} znaków. Łatwo zapamiętać kilka słów, np. „zielony-dach-nad-piecem”.`;

function useChangePassword() {
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => api<MeResponse>('/auth/change-password', { method: 'POST', body }),
    onSuccess: (data) => {
      if (data.user) setCurrentUser(data.user);
    },
  });
}

function PasswordForm({ forced, onDone }: { forced: boolean; onDone: () => void }) {
  const mutation = useChangePassword();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit(e: FormEvent) {
    e.preventDefault();
    const body: ChangePasswordRequest = forced ? { newPassword } : { currentPassword, newPassword };
    const parsed = changePasswordSchema.safeParse(body);
    const next: Record<string, string> = {};
    if (!forced && !currentPassword) next.currentPassword = 'Wpisz obecne hasło.';
    if (!parsed.success) for (const i of parsed.error.issues) next[String(i.path[0])] ??= i.message;
    if (!next.newPassword && repeat !== newPassword) next.repeat = 'Hasła różnią się. Wpisz to samo hasło dwa razy.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      await mutation.mutateAsync(body);
      onDone();
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fields);
    }
  }

  const error = mutation.error;
  const showAlert = error && !(error instanceof ApiError && Object.keys(error.fields).length > 0);

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      {!forced && (
        <PasswordInput
          label="Obecne hasło"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrent(e.target.value)}
          error={errors.currentPassword}
        />
      )}
      <PasswordInput
        label="Nowe hasło"
        autoComplete="new-password"
        hint={NEW_PASSWORD_HINT}
        value={newPassword}
        onChange={(e) => setNew(e.target.value)}
        error={errors.newPassword}
      />
      <PasswordInput
        label="Powtórz nowe hasło"
        autoComplete="new-password"
        value={repeat}
        onChange={(e) => setRepeat(e.target.value)}
        error={errors.repeat}
      />
      {showAlert && <Alert kind="error">{errorMessage(error)}</Alert>}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        block
        icon={<KeyRound aria-hidden="true" />}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? 'Zapisywanie…' : 'Zapisz nowe hasło'}
      </Button>
    </form>
  );
}

/** Shown right after signing in with a temporary password set by the manager. */
export function ForcedPasswordChangePage() {
  const user = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <div className={loginStyles.page}>
      <header className={loginStyles.brand}>
        <BrandMark size={48} />
        <span className={loginStyles.brandName}>Kalendarz wizyt</span>
      </header>
      <main className={loginStyles.card}>
        <h1 className={loginStyles.title}>Ustaw własne hasło</h1>
        <p>
          {user.name}, logujesz się hasłem tymczasowym od kierownika. Wymyśl własne hasło – będziesz go używać przy
          następnych logowaniach.
        </p>
        <PasswordForm
          forced
          onDone={() => {
            toast.success('Hasło zapisane');
            navigate('/kalendarz', { replace: true });
          }}
        />
        <Button variant="ghost" icon={<LogOut aria-hidden="true" />} onClick={() => logout.mutate()}>
          Wyloguj się
        </Button>
      </main>
    </div>
  );
}

/** Voluntary change from Settings. */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <>
      <PageHeader title="Zmiana hasła" back="/ustawienia" />
      <PageBody>
        <div className={styles.narrow}>
          <p className={styles.intro}>Po zmianie hasła inne urządzenia zostaną wylogowane.</p>
          <PasswordForm
            forced={false}
            onDone={() => {
              toast.success('Hasło zostało zmienione');
              navigate('/ustawienia');
            }}
          />
        </div>
      </PageBody>
    </>
  );
}
